import { useEffect, useRef, useState } from 'react';
import {
  useBusinessStore,
  useOnboardingStore,
  useRateStore,
  useResourceStore,
} from '../../../store';
import BusinessInfo from './pages/new-business-info';
import Logo from '../../../assets/svgs/rynopay_logo.svg';
import UltimateBeneficiaries from './pages/ultimate-beneficiaries';
import AppButton from '../../../components/base/button';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  emailRegex,
  ngnRegistrationNumberRegex,
  urlRegex,
} from '../../../constants/constant';
import Loader from '../../../components/base/loader';
import BusinessAddress from './pages/business-adress';
import DocumentUpload from './pages/document-upload';

type StepRefType = {
  saveData: () => void;
  getErrors: () => Record<string, any>;
};

const KybSession = () => {
  const navigate = useNavigate();
  const { updateBusinessInfo, updateBusinessDocuments, loading, getKybStatus } =
    useOnboardingStore();
  const { getCountrySetupData } = useResourceStore();
  const { getActiveCountries } = useRateStore();
  const { getBusiness } = useBusinessStore();
  const [loadingPageResource, setLoadingPageResource] = useState(true);

  const {
    businessInfo,
    businessAddress,
    businessUltimateBeneficiaries,
    businessDocuments,
    onboardingStage,
  } = useOnboardingStore();
  const [step, setStep] = useState(onboardingStage);
  const currentStepRef = useRef<StepRefType | null>(null);

  const handleNext = () => {
    if (currentStepRef.current) {
      currentStepRef.current.saveData(); // Trigger saveData
    }

    // Use a timeout to wait for validation errors to be updated
    setTimeout(() => {
      const currentErrors = currentStepRef.current?.getErrors?.() || {};
      const isValid = Object.keys(currentErrors).length === 0; // Check if there are no errors

      if (isValid) {
        setStep((prev) => Math.min(prev + 1, 5)); // Proceed only if no errors
      } else {
        console.error('Validation failed. Fix the errors before proceeding.');
      }
    }, 100);
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const disableContinueBtn = () => {
    if (step === 1) {
      return (
        !businessInfo.email ||
        (businessInfo.email && !emailRegex.test(businessInfo.email)) ||
        !businessInfo.description ||
        (businessInfo.description && businessInfo.description.length > 1000) ||
        (businessInfo.website && !urlRegex.test(businessInfo.website)) ||
        !businessInfo.registrationNumber ||
        (businessInfo.registrationNumber &&
          !ngnRegistrationNumberRegex.test(businessInfo.registrationNumber)) ||
        !businessInfo.businessType
      );
    } else if (step === 2) {
      const address = businessAddress || {};
      return (
        !address.streetLine1 ||
        !address.city ||
        !address.state ||
        !address.postalCode ||
        (address.postalCode && address.postalCode.length !== 6) ||
        !address.countryCode
      );
    } else if (step === 3) {
      return (
        businessUltimateBeneficiaries.length <= 0 ||
        (businessUltimateBeneficiaries.length > 0 &&
          businessUltimateBeneficiaries.some((ubo) => !ubo.id))
      );
    } else if (step === 4) {
      return !businessDocuments.some((doc) => doc.docId !== null);
    }

    return true;
  };

  const handleSubmit = async () => {
    let response;
    let successMessage = '';
    if (step === 1) {
      handleNext();
      successMessage = 'Business info details saved successfully.';
    } else if (step === 2) {
      response = await updateBusinessInfo();
      successMessage =
        'Business info and address details updated successfully.';
    } else if (step === 3) {
      handleNext();
      successMessage = 'Business ultimate beneficiaries updated successfully.';
    } else if (step === 4) {
      response = await updateBusinessDocuments();
      successMessage = 'Business documents updated successfully.';
    }

    if (response && response.isSuccess) {
      toast.success(successMessage);
      if (step < 4) {
        handleNext();
      } else {
        navigate('/start-kyb');
        getKybStatus();
      }
    }
  };

  useEffect(() => {
    const fetchPageData = async () => {
      setLoadingPageResource(true);
      await getKybStatus();
      await getActiveCountries();
      await getBusiness();
      await getCountrySetupData();
      setLoadingPageResource(false);
    };

    fetchPageData();
  }, []);

  useEffect(() => {
    setStep(onboardingStage);
  }, [onboardingStage]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Fixed Header */}
      <header className="w-full bg-white  fixed top-0 z-10">
        <div className="w-[90%] xl:w-3/4 mx-auto py-4 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <img src={Logo} alt="Logo" className="h-10" />
          </div>
          <div className="flex gap-8 text-sm font-semibold">
            <p className="cursor-pointer">Products</p>
            <p className="cursor-pointer">Features</p>
            <p className="cursor-pointer">Pricing</p>
            <p className="cursor-pointer">FAQ</p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="w-full fixed top-[4rem] z-10">
        <div className="w-full h-1 bg-[#F1F2F4] border border-[#F1F2F4] rounded-md">
          <div
            className="h-full bg-[#0040C2] rounded-md"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="my-32 w-[90%] xl:w-3/4 mx-auto">
        {loadingPageResource ? (
          <Loader
            width="3rem"
            borderTopColor="border-t-blue-500 my-4 mx-auto"
          />
        ) : (
          <div>
            {step === 1 && <BusinessInfo ref={currentStepRef} />}
            {step === 2 && <BusinessAddress ref={currentStepRef} />}
            {step === 3 && <UltimateBeneficiaries ref={currentStepRef} />}
            {step === 4 && <DocumentUpload ref={currentStepRef} />}
          </div>
        )}
      </main>

      {/* Fixed Footer */}
      <footer className="w-full bg-white border-t border-gray-200 fixed bottom-0 z-10">
        <div className="py-4 w-[90%] xl:w-3/4 mx-auto">
          <div className="flex items-center justify-end gap-4">
            {step > 1 && (
              <AppButton
                label="Back"
                type="light"
                clicked={handleBack}
                fullWidth={false}
              />
            )}
            <AppButton
              label="Continue"
              clicked={handleSubmit}
              loading={loading}
              fullWidth={false}
              disabled={disableContinueBtn()}
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KybSession;
